import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { getAuthUrl, PRODUCT_URLS } from '@podus/utils';
import { clearSession, getToken, getUser, saveSession } from '@/lib/session';
import type { SessionUser } from '@/types';

interface AuthContextValue {
    user: SessionUser | null;
    token: string | null;
    isLoggedIn: boolean;
    isAdmin: boolean;
    isAmbassador: boolean;
    login: (token: string, user: SessionUser) => void;
    logout: () => void;
    /** Send an admin to auth.podus.org and bring them back to `/admin/callback`. */
    startAdminSignIn: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [token, setToken] = useState<string | null>(() => getToken());
    const [user, setUser] = useState<SessionUser | null>(() => getUser());

    const login = useCallback((nextToken: string, nextUser: SessionUser) => {
        saveSession(nextToken, nextUser);
        setToken(nextToken);
        setUser(nextUser);
    }, []);

    const logout = useCallback(() => {
        clearSession();
        setToken(null);
        setUser(null);
    }, []);

    /**
     * Admins are ordinary Podus staff accounts, so this platform never holds
     * their credentials — it bounces them through the shared auth service and
     * accepts the token that comes back.
     */
    const startAdminSignIn = useCallback(() => {
        const returnTo = `${PRODUCT_URLS.podusambassador}/admin/callback`;
        window.location.href = `${getAuthUrl()}/sign-in?redirect=${encodeURIComponent(returnTo)}`;
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            token,
            isLoggedIn: Boolean(token && user),
            isAdmin: user?.role === 'admin',
            isAmbassador: user?.role === 'ambassador',
            login,
            logout,
            startAdminSignIn,
        }),
        [user, token, login, logout, startAdminSignIn],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
